import React, { useState, useEffect } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Button,
  Grid,
  Paper,
  Typography,
  IconButton,
  Divider,
  Box,
  TextareaAutosize,
} from '@mui/material';
import { Delete as DeleteIcon, DragHandle as DragHandleIcon } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';

const FieldEditor = ({
  field,
  onUpdate,
  onDelete,
  index,
  isDragging = false,
  dragHandleProps = {},
}) => {
  const [fieldData, setFieldData] = useState({
    id: field?.id || `field-${Date.now()}`,
    type: field?.type || 'text',
    label: field?.label || '',
    required: field?.required || false,
    placeholder: field?.placeholder || '',
    description: field?.description || '',
    options: field?.options || [],
    ...field,
  });

  useEffect(() => {
    onUpdate(fieldData);
  }, [fieldData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFieldData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...fieldData.options];
    newOptions[index] = value;
    setFieldData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const addOption = () => {
    setFieldData(prev => ({
      ...prev,
      options: [...prev.options, '']
    }));
  };

  const removeOption = (index) => {
    const newOptions = fieldData.options.filter((_, i) => i !== index);
    setFieldData(prev => ({
      ...prev,
      options: newOptions
    }));
  };

  const renderFieldOptions = () => {
    switch (fieldData.type) {
      case 'select':
      case 'radio':
      case 'checkbox':
        return (
          <Box sx={{ mt: 2, ml: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Options:
            </Typography>
            {fieldData.options.map((option, idx) => (
              <Box key={idx} display="flex" alignItems="center" mb={1}>
                <TextField
                  size="small"
                  fullWidth
                  value={option}
                  onChange={(e) => handleOptionChange(idx, e.target.value)}
                  placeholder={`Option ${idx + 1}`}
                  sx={{ mr: 1 }}
                />
                <IconButton onClick={() => removeOption(idx)} size="small">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              variant="outlined"
              onClick={addOption}
              sx={{ mt: 1 }}
            >
              Add Option
            </Button>
          </Box>
        );
      case 'textarea':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            placeholder="Enter description (optional)"
            value={fieldData.description}
            onChange={(e) => setFieldData(prev => ({
              ...prev,
              description: e.target.value
            }))}
            sx={{ mt: 1 }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Paper
      elevation={isDragging ? 3 : 1}
      sx={{
        p: 2,
        mb: 2,
        borderLeft: '4px solid',
        borderColor: 'primary.main',
        opacity: isDragging ? 0.8 : 1,
        transition: 'all 0.2s ease',
      }}
    >
      <Box display="flex" alignItems="flex-start">
        <Box {...dragHandleProps} sx={{ mr: 1, cursor: isDragging ? 'grabbing' : 'grab' }}>
          <DragHandleIcon />
        </Box>
        <Box flexGrow={1}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Label"
                name="label"
                value={fieldData.label}
                onChange={handleChange}
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="type"
                  value={fieldData.type}
                  onChange={handleChange}
                  label="Field Type"
                >
                  <MenuItem value="text">Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="email">Email</MenuItem>
                  <MenuItem value="tel">Phone</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="select">Dropdown</MenuItem>
                  <MenuItem value="radio">Radio Buttons</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                  <MenuItem value="textarea">Long Text</MenuItem>
                  <MenuItem value="file">File Upload</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Placeholder"
                name="placeholder"
                value={fieldData.placeholder}
                onChange={handleChange}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2} display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Checkbox
                    name="required"
                    checked={fieldData.required}
                    onChange={handleChange}
                  />
                }
                label="Required"
              />
            </Grid>
          </Grid>

          {renderFieldOptions()}
        </Box>
        <Box ml={1}>
          <IconButton onClick={() => onDelete(fieldData.id)} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
};

export default FieldEditor;
